/**
 * agentsService — the app's side of the agent (MCP) OAuth routes in
 * `functions/oauth/provider.ts`. Every call is authenticated by the user's
 * Supabase access token as bearer; the function decides whose grants they
 * are from the JWT alone.
 */

/**
 * Origin of the OAuth routes. `VITE_APP_BACKEND_URL` is `<functions origin>/api`
 * (`http://localhost:8000/api` in dev, same-origin `/api` in production) and
 * `/oauth/*` sits beside `/api`, so strip the suffix.
 */
export const oauthOrigin = (): string =>
  (import.meta.env.VITE_APP_BACKEND_URL || "/api").replace(/\/api\/?$/, "");

/** One row of `GET /oauth/grants` (see `GrantView` in the function). */
export interface AgentGrant {
  id: string;
  client_id: string;
  client_name: string;
  /** ISO 8601. */
  created_at: string;
  scopes: string[];
}

const failed = (response: Response) =>
  new Error(`The request failed (${response.status}).`);

/** Every agent the signed-in user has connected, newest first. */
export const listGrants = async (
  accessToken: string,
): Promise<AgentGrant[]> => {
  const response = await fetch(`${oauthOrigin()}/oauth/grants`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!response.ok) {
    throw failed(response);
  }
  return ((await response.json()) as { grants: AgentGrant[] }).grants;
};

/** Disconnect one agent: its next call fails with 401. */
export const revokeGrant = async (
  accessToken: string,
  grantId: string,
): Promise<void> => {
  const response = await fetch(
    `${oauthOrigin()}/oauth/grants/${encodeURIComponent(grantId)}`,
    { method: "DELETE", headers: { Authorization: `Bearer ${accessToken}` } },
  );
  if (!response.ok) {
    throw failed(response);
  }
};

/**
 * "Disconnect all agents": revoke every grant of the signed-in user, so each
 * agent's next call fails with 401 and it has to ask for consent again.
 */
export const disconnectAllAgents = async (
  accessToken: string,
): Promise<{ revoked: number }> => {
  const response = await fetch(`${oauthOrigin()}/oauth/grants`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!response.ok) {
    throw failed(response);
  }
  return (await response.json()) as { revoked: number };
};
